/**
 * TenantContext.tsx — Contexto de tenant e white-label do Órbita.
 *
 * Resolve o tenant ativo do usuário autenticado e aplica branding via data-theme (ADR-008).
 * Branding completo vem de tenants/{tenantId}/config/branding (Firestore — aguardando
 * provisionamento). Por ora, usa defaults da plataforma (data-theme="platform").
 *
 * White-label: data-theme="[tenantId]" no <html> sobrescreve tokens semânticos (ADR-008 §2).
 * Troca de tenant: dispara queryClient.clear() para invalidar cache (ADR-002 §4).
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useOrbUser } from "@/features/auth/AuthContext";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface TenantBranding {
  displayName: string;
  logoUrl: string | null;
  /** Tokens CSS customizados do tenant — sobrescritos em runtime */
  themeTokens: Record<string, string>;
  /** Modo de white-label (ADR-008 §3) */
  mode: "platform" | "coBranded" | "whiteLabel";
}

export interface TenantContextValue {
  tenantId: string | null;
  branding: TenantBranding;
  /** Lista de facilities permitidas para o usuário ativo */
  allowedFacilities: string[];
  /** True enquanto resolve o tenant */
  loading: boolean;
}

// ---------------------------------------------------------------------------
// Default — plataforma (sem tenant ou pendente de provisionamento Firestore)
// ---------------------------------------------------------------------------

const PLATFORM_DEFAULT: TenantBranding = {
  displayName: "Órbita",
  logoUrl: null,
  themeTokens: {},
  mode: "platform",
};

// ---------------------------------------------------------------------------
// Contexto
// ---------------------------------------------------------------------------

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const orbUser = useOrbUser();
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState<TenantBranding>(PLATFORM_DEFAULT);

  const tenantId = orbUser?.claims.tenantId ?? null;
  const allowedFacilities = orbUser?.claims.allowedFacilities ?? [];

  useEffect(() => {
    if (!tenantId) {
      // Sem tenant → defaults da plataforma; remove data-theme do html
      document.documentElement.removeAttribute("data-theme");
      setBranding(PLATFORM_DEFAULT);
      setLoading(false);
      return;
    }

    // Aplica data-theme imediatamente com o tenantId (tokens custom virão do Firestore)
    // Por ora usa data-theme="platform" até o Firestore estar provisionado
    document.documentElement.setAttribute("data-theme", "platform");

    // TODO (aguardando provisionamento Firestore): buscar tenants/{tenantId}/config/branding
    // e aplicar os tokens CSS do tenant via document.documentElement.style.setProperty()
    // Quando implementado, remover o timeout abaixo e conectar ao Firestore.
    const fallbackBranding: TenantBranding = {
      displayName: tenantId.charAt(0).toUpperCase() + tenantId.slice(1),
      logoUrl: null,
      themeTokens: {},
      mode: "coBranded",
    };

    setBranding(fallbackBranding);
    setLoading(false);
  }, [tenantId]);

  return (
    <TenantContext.Provider
      value={{ tenantId, branding, allowedFacilities, loading }}
    >
      {children}
    </TenantContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components -- padrão context: provider + hook no mesmo arquivo
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenant deve ser usado dentro de <TenantProvider>.");
  }
  return ctx;
}

/**
 * Leitura FAIL-SAFE do tenantId, sem lançar fora do provider (montagem parcial
 * em teste, modo demo sem shell). Espelha o padrão fail-closed de `useMenuConfig`:
 * ausência de provider devolve `null` (namespace genérico), nunca crasha. Use em
 * preferências de UI que não podem derrubar a tela por falta de provider.
 */
// eslint-disable-next-line react-refresh/only-export-components -- padrão context: provider + hook no mesmo arquivo
export function useTenantIdOptional(): string | null {
  return useContext(TenantContext)?.tenantId ?? null;
}
